import React, { Component } from 'react';
import './styles/error-boundary.css';

/**
 * ErrorBoundary - Captura errores de renderizado en cualquier hijo
 * 
 * Previene que un crash en un componente mate toda la app.
 * Muestra una pantalla de fallback con opción de reintentar.
 * 
 * Uso:
 *   <ErrorBoundary>
 *     <ComponenteQuePodriaFallar />
 *   </ErrorBoundary>
 * 
 * Props opcionales:
 * - onReset: callback al pulsar "Reintentar" (por defecto recarga la página)
 * - fallbackTitle: título personalizado para el error
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log para debugging — en producción podría enviarse a un servicio
    console.error('🔴 ErrorBoundary capturó un error:', error, errorInfo);
  }

  handleReset = () => {
    if (this.props.onReset) {
      this.setState({ hasError: false, error: null, errorInfo: null });
      this.props.onReset();
    } else {
      // Limpiar caches que podrían estar corruptos
      try {
        sessionStorage.removeItem('playerHomeStats');
        sessionStorage.removeItem('dartleague-game-config');
      } catch (e) { /* ignore */ }
      window.location.reload();
    }
  };

  handleGoHome = () => {
    try {
      localStorage.setItem('dartleague-current-view', 'home');
      sessionStorage.removeItem('playerHomeStats');
      sessionStorage.removeItem('dartleague-game-config');
    } catch (e) { /* ignore */ }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === 'development';

      return (
        <div className="error-boundary-container">
          <div className="error-boundary-card">
            <div className="error-boundary-icon">⚠️</div>
            <h2 className="error-boundary-title">
              {this.props.fallbackTitle || 'Algo salió mal'}
            </h2>
            <p className="error-boundary-message">
              Ha ocurrido un error inesperado. No te preocupes, tus datos están a salvo.
            </p>

            {isDev && this.state.error && (
              <details className="error-boundary-details">
                <summary>Detalles del error (solo dev)</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </details>
            )}

            <div className="error-boundary-actions">
              <button className="error-boundary-btn primary" onClick={this.handleReset}>
                🔄 Reintentar
              </button>
              <button className="error-boundary-btn secondary" onClick={this.handleGoHome}>
                🏠 Volver al inicio
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
